<script lang="ts">
    import { flip } from "svelte/animate";

    import { hexToRgb } from "../utils/color";
    import { ANIMATION_DURATION, send, receive } from "../utils/animation";

    import PuzzleNode from "./PuzzleNode.svelte";
    import PuzzleEdge from "./PuzzleEdge.svelte";
    import type { ConstraintDescriptor } from "../types";

    export let featColor: string;
    export let featName: string;
    export let feats: string[];
    export let withEdge: boolean;
    export let constraints: ConstraintDescriptor[];
    export let onFeatsChange: (feats: string[]) => void;

    let dragFrom: number = null;
    let hideNode: number = null;
    let highlightNode: number = null;
    let rootContainer: any = null;
    let dragNode: any = null;

    const handleMouseMove = (e: any) => {
        const { x, y } = rootContainer.getBoundingClientRect();
        dragNode.style.left = `${e.clientX - x - 26}px`;
        dragNode.style.top = `${e.clientY - y - 26}px`;
    };

    const reorder = (i: number, j: number) => {
        if (i !== null && j != null && i !== j) {
            const nextFeats = [...feats];
            const val = nextFeats[i];
            nextFeats.splice(i, 1);
            nextFeats.splice(j, 0, val);
            feats = nextFeats;
        }
    };

    const handleMouseUp = (e) => {
        e.preventDefault();
        dragFrom = null;
        hideNode = null;
        dragNode.style.display = "none";
        document.onmousemove = null;
        document.onmouseup = null;
        onFeatsChange(feats);
    };

    const handleMouseDown = (e: any, i: number) => {
        e.preventDefault();
        document.onmousemove = handleMouseMove;
        dragFrom = i;
        hideNode = i;
        handleMouseMove(e); // update position right away
        dragNode.style.display = "block";
        document.onmouseup = handleMouseUp;
    };

    const handleMouseEnter = (e: any, i: number) => {
        e.preventDefault();
        if (!document.onmousemove) {
            highlightNode = i; // highlight if a drag is not present
        }

        if (dragFrom !== null) {
            hideNode = i;
            reorder(dragFrom, i);
            dragFrom = i;
        }
    };

    const { r, g, b } = hexToRgb(featColor);
</script>

<div
    class="root-container"
    style="background: rgba({r}, {g}, {b}, 0.5);"
    bind:this={rootContainer}
>
    <div class="drag-node-container" bind:this={dragNode}>
        <PuzzleNode
            feat={dragFrom !== null ? feats[dragFrom] : ""}
            visible={true}
            highlight={true}
        />
    </div>
    <div>
        <span class="label">{featName}</span>
    </div>

    <div class="nodes-container">
        {#if withEdge}
            <div class="edges-container">
                {#each Array(feats.length) as _, i}
                    <div in:receive={{ key: i }} out:send={{ key: i }}>
                        <PuzzleEdge />
                    </div>
                {/each}
            </div>
        {/if}

        {#each feats as feat, i (feat)}
            <div
                in:receive={{ key: feat }}
                out:send={{ key: feat }}
                animate:flip={{ duration: ANIMATION_DURATION }}
            >
                <div>
                    <PuzzleNode
                        {feat}
                        visible={i !== hideNode}
                        highlight={highlightNode === i}
                        {constraints}
                    />
                </div>
            </div>
        {/each}

        {#each feats as _, i}
            <div
                class="dragzone"
                on:mouseenter={(e) => handleMouseEnter(e, i)}
                on:mouseleave={() => (highlightNode = null)}
                on:mousedown={(e) => handleMouseDown(e, i)}
                style="width: {100 / feats.length}%; left: {(i / feats.length) *
                    100}%;"
            />
        {/each}
    </div>
</div>

<style lang="scss">
    .root-container {
        position: relative;
        padding: 4px;
        border-radius: 16px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        height: 100%;
        width: 100%;
        gap: 0.5rem;
    }

    .dragzone {
        position: absolute;
        height: 100%;
        z-index: 20;
    }

    .edges-container {
        position: absolute;
        width: 100%;
        height: 95%;
        display: flex;
        justify-content: space-around;
        align-items: flex-end;
        padding-left: 2px;
        padding-right: 2px;
        z-index: 10;
    }
    .nodes-container {
        position: relative;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        cursor: move;
    }

    .label {
        margin-left: 0.5rem;
    }

    .drag-node-container {
        position: absolute;
        left: -100px;
        top: -100px;
        display: none;
        z-index: 15;
    }
</style>
